import { Component, h, Prop, Watch } from '@stencil/core';
import { sortArrayIntoColumns } from '../../global/utility/sort-array-into-columns/sort-array-into-columns';

interface ListItem {
  text: string;
  iconPath?: string;
  subItems?: ListItem[];
}

interface UiCmsListBody {
  alignment?: 'vertical' | 'horizontal';
  style: 'unordered' | 'ordered' | 'icon' | 'checkmark';
  standardIcon?: string;
  columns?: number;
  items: ListItem[];
}

@Component({
  tag: 'ui-cms-list',
  styleUrl: 'ui-cms-list.scss',
})
export class UiCmsList {
  @Prop({ mutable: true }) body: UiCmsListBody = {} as any;

  @Prop() inverted: boolean;

  @Prop({ attribute: 'body' }) utilityBody: string;

  private sortedColumns: ListItem[][];

  private withIcon: boolean;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;

    const { columns, items, style } = this.body;

    this.sortedColumns = sortArrayIntoColumns(columns, items) as ListItem[][];
    // DEV-NOTE: The list style 'checkmark' is equivalent to 'icon' but differentiated in BE.
    const isCheckmark = style === 'checkmark';
    this.withIcon = style === 'icon' || isCheckmark;
  }

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
  }

  private getIconName(
    style: UiCmsListBody['style'],
    item: ListItem,
    standardIcon: UiCmsListBody['standardIcon']
  ): string {
    const isCheckmark = style === 'checkmark';
    const isIcon = style === 'icon';
    let iconName: string;

    if (isCheckmark) {
      iconName = 'union-investment/content/circle-hook';
    }

    if (isIcon && standardIcon && standardIcon.length) {
      iconName = standardIcon;
    }

    if (isIcon && item.iconPath && item.iconPath.length) {
      iconName = item.iconPath;
    }

    return iconName;
  }

  render() {
    const ListTag = this.body.style === 'ordered' ? 'ol' : 'ul';

    return (
      <div
        class={{
          [`columns columns-${this.body.columns}`]: this.body.columns > 1,
        }}
      >
        {this.sortedColumns.map((column: ListItem[], index) => (
          <ListTag
            key={`column-${index}`}
            class={{
              'sd-list': true,
              'sd-list--icon': this.withIcon,
              'sd-list--horizontal': this.body.alignment === 'horizontal',
              'sd-list--inverted': this.inverted,
            }}
          >
            {column.map((listItem: ListItem, listIndex) => (
              <li key={`listItem-${listIndex}`}>
                {this.withIcon && (
                  <sd-icon
                    name={this.getIconName(this.body.style, listItem, this.body.standardIcon)}
                    library={window.ui.cmsState.iconLibrary}
                  ></sd-icon>
                )}
                {listItem.text}
                {listItem.subItems && (
                  <ListTag>
                    {listItem.subItems?.map((innerListItem: ListItem, innerListIndex) => (
                      <li key={`listItem-${innerListIndex}`}>
                        {this.withIcon && (
                          <sd-icon
                            name={this.getIconName(this.body.style, innerListItem, this.body.standardIcon)}
                            library={window.ui.cmsState.iconLibrary}
                          ></sd-icon>
                        )}
                        {innerListItem.text}
                      </li>
                    ))}
                  </ListTag>
                )}
              </li>
            ))}
          </ListTag>
        ))}
      </div>
    );
  }
}
